import { IconNames } from '@blueprintjs/icons';
import { BenefitTypeDisplayTextMap, BenefitTypes } from '@strata/constants';
import { DateFormats, formatDate, sortDesc } from '@strata/helper-functions';
import { ActionButtonText } from '@strata/react-buttons';
import { FormContainer } from '@strata/react-form-container';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { FieldArray, Formik, getIn } from 'formik';
import PropTypes from 'prop-types';
import { useCallback, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { PayerCapabilityRow } from 'pages/npiManagement/payerCapabilities/PayerCapabilityRow';
import { CREATE_ROUTE } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { put, useGet } from 'services/Http';

const SortedBenefits = Object.values(BenefitTypes).sort(sortDesc);

export const PayerCapabilityForm = ({
    formTitle,
    isLoading,
    activeCapabilities = undefined,
    payerOptions = undefined,
    payerSegmentOptions = undefined,
}) => {
    const { npi, payerId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const isCreate = payerId === CREATE_ROUTE;

    const [selectedPayerId, setSelectedPayerId] = useState(!isCreate ? payerId : undefined);
    const marketOptions = useGet(
        selectedPayerId ? `/reference/payers/${selectedPayerId}/lob_segments` : null,
    );

    const filterSegmentOptionsByPayer = useCallback(
        (id) => payerSegmentOptions?.filter((segment) => id === segment.payer_id),
        [payerSegmentOptions],
    );

    return (
        <Formik
            initialValues={{
                payerId: !isCreate ? payerId : undefined,
                activeCapabilities,
                capabilitiesToRemove: [],
            }}
            enableReinitialize
            validationSchema={Yup.object({
                payerId: Yup.string().required(validationStrings.requiredField.errorMessage),
                activeCapabilities: Yup.object(
                    Object.fromEntries(
                        SortedBenefits.map((benefit) => [
                            benefit,
                            Yup.array(PayerCapabilityRow.validationSchema),
                        ]),
                    ),
                ),
                capabilitiesToRemove: Yup.array(),
            })}
            onSubmit={async (values) => {
                // Combine 'deleted' and updated entries to send back to API
                // deleted entries that are active should just be updated with end_date as yesterday
                // deleted future entries should actually be removed
                const capabilitiesToAddOrUpdate = Object.values(values.activeCapabilities)
                    .flat()
                    .map((cap) => ({
                        ...cap,
                        payer_id: values.payerId,
                        // Strip the frontend generated history_id from new caps
                        history_id: cap.is_new ? undefined : cap.history_id,
                    }));
                const capabilitiesToDelete = [];

                values.capabilitiesToRemove.forEach((cap) => {
                    if (dayjs().isAfter(cap.start_date)) {
                        capabilitiesToAddOrUpdate.push({
                            ...cap,
                            end_date: formatDate(
                                dayjs().subtract(1, 'day'),
                                DateFormats.HYPHEN_DATE,
                            ),
                        });
                    } else {
                        capabilitiesToDelete.push(cap);
                    }
                });

                try {
                    await put(`/reference/capabilities/${npi}/payer_benefit`, {
                        added_capabilities: capabilitiesToAddOrUpdate,
                        removed_capabilities: capabilitiesToDelete,
                    });

                    SuccessToast({
                        message: 'Payer Capabilities successfully updated.',
                    });

                    navigate({
                        pathname: `/edit-supplier/${npi}`,
                        hash: location.hash,
                    });
                } catch (err) {
                    handleError('Error updating payer capabilities.', err);
                }
            }}
        >
            {(formik) => {
                const segmentsForPayer = filterSegmentOptionsByPayer(formik.values.payerId);
                return (
                    <FormContainer
                        formTitle={formTitle}
                        onFormSubmit={formik.handleSubmit}
                        isLoading={isLoading}
                        isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                        onSecondaryButtonClick={() =>
                            navigate({
                                pathname: `/edit-supplier/${npi}`,
                                hash: location.hash,
                            })
                        }
                        formLeftContent={
                            <>
                                {isCreate && (
                                    <SuggestSingleSelectFormik
                                        id="payerId"
                                        label="Payer"
                                        placeholderText="Select Payer"
                                        required
                                        values={payerOptions}
                                        selectedItem={payerOptions?.find(
                                            (payer) =>
                                                payer.secondaryData === formik.values.payerId,
                                        )}
                                        onChange={(newVal) => {
                                            formik.setValues({
                                                payerId: newVal?.secondaryData,
                                                activeCapabilities: Object.fromEntries(
                                                    SortedBenefits.map((benefit) => [benefit, []]),
                                                ),
                                                capabilitiesToRemove: [],
                                            });
                                            setSelectedPayerId(newVal?.secondaryData);
                                        }}
                                    />
                                )}
                                {SortedBenefits.map((benefit) => (
                                    <FieldArray
                                        key={benefit}
                                        name={`activeCapabilities.${benefit}`}
                                    >
                                        {(arrayHelpers) => (
                                            <div className="sds_payer-capabilities__form-section">
                                                <div className="sds_payer-capabilities__form-title">
                                                    {BenefitTypeDisplayTextMap[benefit]} Benefit
                                                </div>
                                                {(getIn(formik.values, arrayHelpers.name) ?? [])
                                                    .length === 0 && (
                                                    <div className="sds_payer-capabilities__form-section--no-capabilities">
                                                        {`No ${BenefitTypeDisplayTextMap[
                                                            benefit
                                                        ].toLocaleLowerCase()} benefit capabilities configured.`}
                                                    </div>
                                                )}
                                                {getIn(formik.values, arrayHelpers.name)?.map(
                                                    (cap, i) => (
                                                        <PayerCapabilityRow
                                                            key={cap.history_id}
                                                            segmentsForPayer={segmentsForPayer}
                                                            marketOptions={marketOptions}
                                                            benefit={benefit}
                                                            index={i}
                                                            cap={cap}
                                                            arrayHelpers={arrayHelpers}
                                                        />
                                                    ),
                                                )}
                                                <ActionButtonText
                                                    label={`Add ${BenefitTypeDisplayTextMap[benefit]} Capability`}
                                                    leftIcon={IconNames.ADD}
                                                    onClick={() =>
                                                        arrayHelpers.push({
                                                            history_id: uuidv4(),
                                                            benefit,
                                                            is_new: true,
                                                            no_market: false,
                                                        })
                                                    }
                                                />
                                            </div>
                                        )}
                                    </FieldArray>
                                ))}
                            </>
                        }
                    />
                );
            }}
        </Formik>
    );
};

PayerCapabilityForm.propTypes = {
    formTitle: PropTypes.string.isRequired,
    isLoading: PropTypes.bool.isRequired,
    activeCapabilities: PropTypes.shape({}),
    payerOptions: PropTypes.arrayOf(PropTypes.shape({})),
    payerSegmentOptions: PropTypes.arrayOf(PropTypes.shape({})),
};
